import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  TrendingDown, RefreshCw, Activity, DollarSign,
  BarChart2, Target, Wifi, WifiOff,
  ArrowUpRight, ArrowDownRight, Layers, Clock,
  AlertTriangle, CheckCircle, Eye, EyeOff, FileText, X,
} from 'lucide-react';
import TradingChart from '../components/TradingChart';
import type { AgentLegendEntry, MultiSymbolEntry } from '../components/TradingChart';
import * as svc from '../services/tradingService';
import { TradingWebSocket } from '../services/websocketService';
import {
  OhlcvCandle, TradeMarker, Position, TradeRecord,
  PnlSummary, AgentPortfolio, WsEvent,
} from '../types/trading';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (v: number | null | undefined, decimals = 2, prefix = '$') => {
  if (v == null) return '—';
  const s = Math.abs(v).toFixed(decimals);
  const sign = v >= 0 ? '+' : '-';
  return `${sign}${prefix}${Number(s).toLocaleString()}`;
};

const fmtPct = (v: number | null | undefined) => {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};

const pnlColor = (v: number | null | undefined) =>
  !v || v === 0 ? 'text-slate-400' : v > 0 ? 'text-green-400' : 'text-red-400';

// Symbol presets
const CRYPTO_SYMBOLS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT', 'DOGE/USDT'];
const EQUITY_SYMBOLS = ['AAPL', 'TSLA', 'NVDA', 'MSFT', 'GOOGL', 'SPY', 'QQQ'];
const TIMEFRAMES     = ['1m', '5m', '15m', '1h', '4h', '1d'];
const EXCHANGES_CRYPTO  = ['kraken', 'binance', 'coinbase'];
const EXCHANGES_EQUITY  = ['alpaca'];

// Distinct colors for agent overlays on the dark chart background
const AGENT_COLORS = [
  '#06b6d4', // cyan
  '#a855f7', // purple
  '#f97316', // orange
  '#14b8a6', // teal
  '#f43f5e', // rose
  '#f59e0b', // amber
  '#84cc16', // lime
  '#6366f1', // indigo
  '#ec4899', // pink
  '#10b981', // emerald
];

// ─── sub-components ─────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color?: string;
  icon: React.ReactNode;
}> = ({ label, value, sub, color = 'text-white', icon }) => (
  <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
    <div className="p-2 rounded-lg bg-slate-700/50 text-slate-400">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-lg font-bold leading-none ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  </div>
);

const Badge: React.FC<{ label: string; variant?: 'paper' | 'live' | 'neutral' }> = ({ label, variant = 'neutral' }) => {
  const cls = variant === 'paper'   ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
            : variant === 'live'    ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
            : 'bg-slate-700 text-slate-300 border-slate-600';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>{label}</span>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const TradingDashboard: React.FC = () => {
  // Chart state
  // symbolInput is the raw text in the input (may be comma-separated)
  const [symbolInput, setSymbolInput] = useState('BTC/USDT');
  // symbols is the parsed, committed list
  const [symbols, setSymbols]         = useState<string[]>(['BTC/USDT']);
  const [exchange, setExchange]       = useState('kraken');
  const [timeframe, setTf]            = useState('1h');
  const [assetType, setAsset]         = useState<'crypto' | 'equities'>('crypto');
  const [paperFilter, setPaper]       = useState<boolean | undefined>(undefined);

  // Convenience: the "primary" symbol (first in list) for single-symbol APIs
  const symbol = symbols[0] ?? '';

  // Agent overlay selection — empty = show all agents' markers
  const [overlayAgents, setOverlayAgents] = useState<string[]>([]);

  // Data state
  const [candles, setCandles]     = useState<OhlcvCandle[]>([]);
  const [markers, setMarkers]     = useState<TradeMarker[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [trades, setTrades]       = useState<TradeRecord[]>([]);
  const [pnl, setPnl]             = useState<PnlSummary | null>(null);
  const [portfolio, setPortfolio] = useState<AgentPortfolio[]>([]);

  const [loadingChart, setLoadingChart] = useState(false);
  const [chartError, setChartError]     = useState<string | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | undefined>();
  const [multiSymbolData, setMultiSymbolData] = useState<MultiSymbolEntry[]>([]);
  const [configuredExchanges, setConfiguredExchanges] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab]       = useState<'positions' | 'trades'>('positions');

  // Request ID ref — prevents stale responses from overwriting newer data
  const loadIdRef = useRef(0);

  // Trade reasoning modal
  const [reasoningTrade, setReasoningTrade] = useState<TradeRecord | null>(null);

  // WebSocket
  const [wsStatus, setWsStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [liveAlerts, setLiveAlerts] = useState<{id: number; text: string}[]>([]);
  const alertIdRef = useRef(0);
  const wsRef = useRef<TradingWebSocket | null>(null);
  const loadTableDataRef = useRef<(() => Promise<void>) | null>(null);

  // ── Derived: stable color per agent (by portfolio order) ────────────────
  const agentColorMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    portfolio.forEach((p, i) => {
      map[p.agent_id] = AGENT_COLORS[i % AGENT_COLORS.length];
    });
    return map;
  }, [portfolio]);

  // Derived: markers filtered to selected agents (empty selection = show all) — single-symbol mode
  const displayedMarkers = useMemo<TradeMarker[]>(() => {
    if (!Array.isArray(markers)) return [];
    if (overlayAgents.length === 0) return markers;
    return markers.filter(m => overlayAgents.includes(m.agent_id));
  }, [markers, overlayAgents]);

  // Derived: multi-symbol entries enriched with per-symbol filtered markers
  const displayedMultiSymbolData = useMemo<MultiSymbolEntry[]>(() => {
    if (!Array.isArray(multiSymbolData)) return [];
    return multiSymbolData.map(entry => ({
      ...entry,
      candles: Array.isArray(entry.candles) ? entry.candles : [],
      markers: entry.markers
        ? (overlayAgents.length === 0
            ? entry.markers
            : entry.markers.filter(m => overlayAgents.includes(m.agent_id)))
        : [],
    }));
  }, [multiSymbolData, overlayAgents]);

  // Derived: positions filtered to selected agents
  const displayedPositions = useMemo<Position[]>(() => {
    if (!Array.isArray(positions)) return [];
    if (overlayAgents.length === 0) return positions;
    return positions.filter(p => overlayAgents.includes(p.agent_id));
  }, [positions, overlayAgents]);

  // Derived: trades filtered to selected agents
  const displayedTrades = useMemo<TradeRecord[]>(() => {
    if (!Array.isArray(trades)) return [];
    if (overlayAgents.length === 0) return trades;
    return trades.filter(t => overlayAgents.includes(t.agent_id));
  }, [trades, overlayAgents]);

  // Derived: P&L recomputed from filtered trades/positions when agents are selected
  const displayedPnl = useMemo<PnlSummary | null>(() => {
    if (overlayAgents.length === 0) return pnl;
    if (!Array.isArray(displayedTrades) || !Array.isArray(displayedPositions)) return pnl;

    const filledTrades = displayedTrades.filter(t => t.status === 'filled' && t.pnl != null);
    const totalTrades = filledTrades.length;
    if (totalTrades === 0 && displayedPositions.length === 0) return null;

    const winningTrades = filledTrades.filter(t => (t.pnl ?? 0) > 0);
    const losingTrades = filledTrades.filter(t => (t.pnl ?? 0) < 0);
    const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0;
    const realizedPnl = filledTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0);
    const unrealizedPnl = displayedPositions.reduce((sum, p) => sum + p.unrealized_pnl, 0);

    const sumWins = winningTrades.reduce((s, t) => s + (t.pnl ?? 0), 0);
    const sumLosses = losingTrades.reduce((s, t) => s + (t.pnl ?? 0), 0);
    const avgWin = winningTrades.length > 0 ? sumWins / winningTrades.length : 0;
    const avgLoss = losingTrades.length > 0 ? Math.abs(sumLosses) / losingTrades.length : 0;
    const profitFactor = sumLosses !== 0 ? sumWins / Math.abs(sumLosses) : null;

    let peak = 0, maxDd = 0, running = 0;
    for (const t of filledTrades) {
      running += t.pnl ?? 0;
      if (running > peak) peak = running;
      const dd = peak - running;
      if (dd > maxDd) maxDd = dd;
    }

    return {
      total_trades: totalTrades,
      winning_trades: winningTrades.length,
      losing_trades: losingTrades.length,
      win_rate: winRate,
      realized_pnl: realizedPnl,
      unrealized_pnl: unrealizedPnl,
      total_pnl: realizedPnl + unrealizedPnl,
      avg_win: avgWin,
      avg_loss: avgLoss,
      profit_factor: profitFactor,
      max_drawdown: maxDd,
      open_positions: displayedPositions.length,
    };
  }, [pnl, overlayAgents, displayedTrades, displayedPositions]);

  // Derived: legend entries passed into the chart header
  const chartAgentLegend = useMemo<AgentLegendEntry[]>(() =>
    overlayAgents.map(id => ({
      id,
      name: portfolio.find(p => p.agent_id === id)?.agent_name ?? id.slice(0, 8),
      color: agentColorMap[id] ?? '#94a3b8',
    })),
    [overlayAgents, portfolio, agentColorMap]
  );

  const toggleAgentOverlay = (agentId: string) => {
    setOverlayAgents(prev =>
      prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]
    );
  };

  // ── WebSocket setup ───────────────────────────────────────────────────────
  useEffect(() => {
    const ws = new TradingWebSocket(
      (event: WsEvent) => {
        if (event.type === 'trade_created') {
          const d = event.data;
          const id = ++alertIdRef.current;
          setLiveAlerts(prev =>
            [{ id, text: `${d.agent_name ?? 'Manual'} ${d.side.toUpperCase()} ${d.symbol} @ $${d.price.toFixed(4)}` }, ...prev]
          );
          loadChartData();
          loadTableDataRef.current?.();
        }
        if (event.type === 'price_update') {
          const d = event.data;
          setPositions(prev =>
            prev.map(p =>
              p.symbol === d.symbol
                ? {
                    ...p,
                    current_price: d.price,
                    market_value: Math.abs(p.quantity) * d.price,
                    unrealized_pnl: (d.price - p.avg_entry_price) * Math.abs(p.quantity),
                    unrealized_pnl_pct: p.avg_entry_price ? ((d.price - p.avg_entry_price) / p.avg_entry_price) * 100 : 0,
                  }
                : p
            )
          );
        }
        if (event.type === 'agent_signal') {
          const d = event.data;
          const dot = d.signal === 'buy' ? '🟢' : d.signal === 'sell' ? '🔴' : '🟡';
          const id = ++alertIdRef.current;
          setLiveAlerts(prev =>
            [{ id, text: `${dot} ${d.agent_name}: ${d.signal.toUpperCase()} ${d.symbol} (${(d.confidence * 100).toFixed(0)}% conf)` }, ...prev]
          );
        }
      },
      setWsStatus,
    );
    ws.connect();
    wsRef.current = ws;
    return () => ws.disconnect();
  }, []); // eslint-disable-line

  // ── Exchange credential status ─────────────────────────────────────────────
  useEffect(() => {
    svc.listExchangeCredentials()
      .then(creds => {
        if (!Array.isArray(creds)) return;
        setConfiguredExchanges(
          new Set(creds.filter(c => c.is_active).map(c => c.exchange_id))
        );
      })
      .catch(console.error);
  }, [exchange]);

  // ── Data loaders ──────────────────────────────────────────────────────────

  // Stable set of colors assigned to symbols in order
  const MULTI_COLORS = [
    '#38bdf8', // sky
    '#a78bfa', // violet
    '#fb923c', // orange
    '#34d399', // emerald
    '#f472b6', // pink
    '#facc15', // yellow
    '#60a5fa', // blue
    '#f87171', // red
  ];

  const loadChartData = useCallback(async () => {
    const id = ++loadIdRef.current;
    setLoadingChart(true);
    setChartError(null);
    setCurrentPrice(undefined);

    if (symbols.length > 1) {
      // ── Multi-symbol: fetch all in parallel, markers included per symbol
      try {
        const results = await Promise.allSettled(
          symbols.map(sym =>
            svc.getOhlcv({ symbol: sym, exchange, timeframe, limit: 200, is_paper: paperFilter })
          )
        );
        if (id !== loadIdRef.current) return;

        const entries: MultiSymbolEntry[] = [];
        const errors: string[] = [];
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && !r.value.candle_error) {
            entries.push({
              symbol: symbols[i],
              candles: Array.isArray(r.value.candles) ? r.value.candles : [],
              color: MULTI_COLORS[i % MULTI_COLORS.length],
              markers: Array.isArray(r.value.markers) ? r.value.markers : [],
            });
          } else {
            const msg = r.status === 'rejected'
              ? r.reason?.response?.data?.detail ?? `Failed: ${symbols[i]}`
              : r.value.candle_error ?? `No data: ${symbols[i]}`;
            errors.push(msg);
          }
        });

        setMultiSymbolData(entries);
        setCandles([]);
        setMarkers([]);
        if (errors.length) setChartError(errors.join(' | '));
        else setChartError(null);
      } catch (e: any) {
        if (id !== loadIdRef.current) return;
        setMultiSymbolData([]);
        setChartError(e?.response?.data?.detail || 'Failed to load chart data.');
      } finally {
        if (id === loadIdRef.current) setLoadingChart(false);
      }
    } else {
      // ── Single symbol: candlestick + markers
      try {
        const resp = await svc.getOhlcv({ symbol, exchange, timeframe, limit: 200, is_paper: paperFilter });
        if (id !== loadIdRef.current) return;
        setMultiSymbolData([]);
        if (resp.candle_error) {
          setChartError(resp.candle_error);
          setCandles([]);
          setMarkers([]);
        } else {
          setCandles(Array.isArray(resp.candles) ? resp.candles : []);
          setMarkers(Array.isArray(resp.markers) ? resp.markers : []);
        }
      } catch (e: any) {
        if (id !== loadIdRef.current) return;
        setMultiSymbolData([]);
        setCandles([]);
        setMarkers([]);
        setChartError(e?.response?.data?.detail || 'Failed to load chart data. Check your exchange settings.');
      } finally {
        if (id === loadIdRef.current) setLoadingChart(false);
      }
    }
  }, [symbols, exchange, timeframe, paperFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time price polling ────────────────────────────────────────────────
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const resp = await svc.getCurrentPrice({ symbol, exchange });
        setCurrentPrice(resp.price);
      } catch { /* silently ignore */ }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 15000);
    return () => clearInterval(interval);
  }, [symbol, exchange]);

  const loadTableData = useCallback(async () => {
    try {
      await svc.refreshPositionPrices().catch(() => {});
      const [pos, tr, pnlSummary, port] = await Promise.all([
        svc.getPositions({ status: 'open', is_paper: paperFilter }),
        svc.getTrades({ is_paper: paperFilter, limit: 100 }),
        svc.getPnlSummary({ is_paper: paperFilter }),
        svc.getPortfolio({ is_paper: paperFilter }),
      ]);
      setPositions(Array.isArray(pos) ? pos : []);
      setTrades(Array.isArray(tr) ? tr : []);
      setPnl(pnlSummary ?? null);
      setPortfolio(Array.isArray(port) ? port : []);
    } catch (e) {
      console.error('Table load error', e);
    }
  }, [paperFilter]);

  useEffect(() => { loadChartData(); }, [loadChartData]);

  useEffect(() => {
    loadTableDataRef.current = loadTableData;
  }, [loadTableData]);

  useEffect(() => {
    loadTableData();
    const interval = setInterval(loadTableData, 30000);
    return () => clearInterval(interval);
  }, [loadTableData]);

  // ── Position management ───────────────────────────────────────────────────
  const handleClosePosition = async (posId: string) => {
    try {
      await svc.closePosition(posId);
      await loadTableData();
      await loadChartData();
    } catch (e) { console.error('Close position error', e); }
  };

  const handleRefreshPrices = async () => {
    try {
      await svc.refreshPositionPrices();
      await loadTableData();
    } catch (e) { console.error('Refresh error', e); }
  };

  const handleAssetSwitch = (type: 'crypto' | 'equities') => {
    setAsset(type);
    if (type === 'crypto') {
      setSymbolInput('BTC/USDT');
      setSymbols(['BTC/USDT']);
      setExchange('kraken');
    } else {
      setSymbolInput('AAPL');
      setSymbols(['AAPL']);
      setExchange('alpaca');
    }
  };

  // Parse and commit a raw symbol input string
  const commitSymbolInput = (raw: string) => {
    const parsed = raw
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(Boolean);
    if (parsed.length > 0) {
      setSymbols(parsed);
      setSymbolInput(parsed.join(', '));
    }
  };

  const isMultiChart = symbols.length > 1;
  const isExchangeConfigured = assetType === 'crypto' || configuredExchanges.has(exchange);
  const symbolList   = assetType === 'crypto' ? CRYPTO_SYMBOLS : EQUITY_SYMBOLS;
  const exchangeList = assetType === 'crypto' ? EXCHANGES_CRYPTO : EXCHANGES_EQUITY;

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Trading Dashboard</h1>
          <p className="text-slate-400 text-sm mt-0.5">Forward testing &amp; live trading performance</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${
            wsStatus === 'connected'  ? 'text-green-400 bg-green-400/10 border-green-400/30' :
            wsStatus === 'connecting' ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' :
            'text-slate-400 bg-slate-700/50 border-slate-600'
          }`}>
            {wsStatus === 'connected' ? <Wifi size={12} /> : <WifiOff size={12} />}
            {wsStatus === 'connected' ? 'WS Live' : wsStatus === 'connecting' ? 'Connecting…' : 'WS Offline'}
          </span>
          <button
            onClick={() => { loadChartData(); loadTableData(); }}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg border border-slate-600 transition"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Live alerts ── */}
      {liveAlerts.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">{liveAlerts.length} alert{liveAlerts.length !== 1 ? 's' : ''}</span>
            <button
              onClick={() => setLiveAlerts([])}
              className="text-xs text-slate-500 hover:text-slate-300 transition"
            >
              Clear all
            </button>
          </div>
          {liveAlerts.map((alert, i) => (
            <div key={alert.id} className={`flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 ${
              i === 0 ? 'border-blue-500/30' : ''
            }`}>
              <Activity size={14} className={`flex-shrink-0 ${i === 0 ? 'text-blue-400 animate-pulse' : 'text-slate-600'}`} />
              <span className={`text-xs flex-1 ${i === 0 ? 'text-white' : 'text-slate-400'}`}>{alert.text}</span>
              <button
                onClick={() => setLiveAlerts(prev => prev.filter(a => a.id !== alert.id))}
                className="text-slate-500 hover:text-slate-300 transition flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Controls row ── */}
      <div className="flex flex-wrap gap-2 bg-slate-800/40 border border-slate-700 rounded-xl p-3">
        {/* Asset type */}
        <div className="flex rounded-lg overflow-hidden border border-slate-600">
          {(['crypto', 'equities'] as const).map(t => (
            <button key={t} onClick={() => handleAssetSwitch(t)}
              className={`px-3 py-1.5 text-xs font-medium transition ${
                assetType === t ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {t === 'crypto' ? '₿ Crypto' : '📈 Equities'}
            </button>
          ))}
        </div>

        {/* Symbol — supports comma-delimited multi-symbol */}
        <div className="relative flex items-center gap-1">
          <input
            type="text"
            value={symbolInput}
            onChange={e => setSymbolInput(e.target.value.toUpperCase())}
            onBlur={e => commitSymbolInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitSymbolInput((e.target as HTMLInputElement).value); }}
            list="symbol-presets"
            placeholder={assetType === 'crypto' ? 'BTC/USDT, ETH/USDT' : 'AAPL, NVDA'}
            title="Enter one or more symbols separated by commas"
            className={`bg-slate-700 border text-white text-xs rounded-lg px-3 py-1.5 font-mono uppercase transition ${
              isMultiChart ? 'border-sky-500/60 w-52' : 'border-slate-600 w-36'
            }`}
          />
          <datalist id="symbol-presets">
            {symbolList.map(s => <option key={s} value={s} />)}
          </datalist>
          {isMultiChart && (
            <span className="text-xs text-sky-400 whitespace-nowrap font-medium">
              {symbols.length} symbols
            </span>
          )}
        </div>

        {/* Exchange */}
        <div className="flex items-center gap-1.5">
          <select value={exchange} onChange={e => setExchange(e.target.value)}
            className="bg-slate-700 border border-slate-600 text-white text-xs rounded-lg px-3 py-1.5"
          >
            {exchangeList.map(ex => <option key={ex} value={ex}>{ex}</option>)}
          </select>
          {assetType === 'equities' && (
            isExchangeConfigured
              ? <span title="Exchange credentials configured"><CheckCircle size={14} className="text-green-400" /></span>
              : <span title="Exchange credentials not configured"><AlertTriangle size={14} className="text-orange-400" /></span>
          )}
        </div>

        {/* Timeframe */}
        <div className="flex rounded-lg overflow-hidden border border-slate-600">
          {TIMEFRAMES.map(tf => (
            <button key={tf} onClick={() => setTf(tf)}
              className={`px-2.5 py-1.5 text-xs font-mono font-medium transition ${
                timeframe === tf ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

        {/* Paper / Live filter */}
        <div className="flex rounded-lg overflow-hidden border border-slate-600 ml-auto">
          {[
            { label: 'All', val: undefined },
            { label: '📄 Paper', val: true },
            { label: '⚡ Live', val: false },
          ].map(({ label, val }) => (
            <button key={label} onClick={() => setPaper(val)}
              className={`px-3 py-1.5 text-xs font-medium transition ${
                paperFilter === val ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Exchange not configured banner ── */}
      {!isExchangeConfigured && (
        <div className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-2.5">
          <AlertTriangle size={14} className="text-orange-400 flex-shrink-0" />
          <span className="text-xs text-orange-300">
            <strong>{exchange.charAt(0).toUpperCase() + exchange.slice(1)}</strong> credentials not configured
            — chart data and live trading require API keys.
          </span>
          <a href="/exchange-settings"
            className="ml-auto text-xs text-orange-300 underline hover:text-orange-200 transition whitespace-nowrap"
          >
            Configure Exchange →
          </a>
        </div>
      )}

      {/* ── Chart error banner ── */}
      {chartError && !loadingChart && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5">
          <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
          <span className="text-xs text-red-300">{chartError}</span>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Total P&L"
          value={displayedPnl ? fmt(displayedPnl.total_pnl) : '—'}
          sub={displayedPnl ? `Realized ${fmt(displayedPnl.realized_pnl)} / Unrealized ${fmt(displayedPnl.unrealized_pnl)}` : undefined}
          color={displayedPnl ? pnlColor(displayedPnl.total_pnl) : 'text-white'}
          icon={<DollarSign size={16} />}
        />
        <StatCard
          label="Win Rate"
          value={displayedPnl && displayedPnl.win_rate != null ? `${displayedPnl.win_rate.toFixed(1)}%` : '—'}
          sub={displayedPnl ? `${displayedPnl.winning_trades ?? 0}W / ${displayedPnl.losing_trades ?? 0}L of ${displayedPnl.total_trades ?? 0}` : undefined}
          color={displayedPnl && (displayedPnl.win_rate ?? 0) >= 50 ? 'text-green-400' : 'text-red-400'}
          icon={<Target size={16} />}
        />
        <StatCard
          label="Open Positions"
          value={String(displayedPositions.length)}
          sub={`Unrealized ${displayedPnl ? fmt(displayedPnl.unrealized_pnl) : '—'}`}
          icon={<Layers size={16} />}
        />
        <StatCard
          label="Max Drawdown"
          value={displayedPnl ? fmt(displayedPnl.max_drawdown, 2, '-$') : '—'}
          sub={displayedPnl ? `Profit Factor: ${displayedPnl.profit_factor != null ? displayedPnl.profit_factor.toFixed(2) : '—'}` : undefined}
          color="text-red-400"
          icon={<TrendingDown size={16} />}
        />
      </div>

      {/* ── Agent Overlay Selector (appears when agents exist) ── */}
      {portfolio.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-slate-800/40 border border-slate-700 rounded-xl px-4 py-2.5">
          <span className="text-xs text-slate-400 font-medium mr-1 whitespace-nowrap">Agent Filter:</span>

          {/* "All Agents" resets to showing everything */}
          <button
            onClick={() => setOverlayAgents([])}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition ${
              overlayAgents.length === 0
                ? 'bg-slate-600 border-slate-500 text-white'
                : 'bg-transparent border-slate-600 text-slate-400 hover:text-white hover:border-slate-500'
            }`}
          >
            All Agents
          </button>

          {/* One chip per agent */}
          {portfolio.map((p, i) => {
            const color = AGENT_COLORS[i % AGENT_COLORS.length];
            const active = overlayAgents.includes(p.agent_id);
            return (
              <button
                key={p.agent_id}
                onClick={() => toggleAgentOverlay(p.agent_id)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition"
                style={active
                  ? { backgroundColor: color + '22', borderColor: color + '99', color: '#fff' }
                  : { backgroundColor: 'transparent', borderColor: '#475569', color: '#94a3b8' }
                }
              >
                <span style={{ color: active ? color : '#475569', fontSize: '10px' }}>●</span>
                {p.agent_name}
                <span className="opacity-60">({p.total_trades})</span>
              </button>
            );
          })}

          {/* Marker count when filtered */}
          {overlayAgents.length > 0 && (
            <span className="text-xs text-slate-500 ml-auto whitespace-nowrap">
              {isMultiChart
                ? `${displayedMultiSymbolData.reduce((n, e) => n + (e.markers?.length ?? 0), 0)} marker${displayedMultiSymbolData.reduce((n, e) => n + (e.markers?.length ?? 0), 0) !== 1 ? 's' : ''} shown`
                : `${displayedMarkers.length} marker${displayedMarkers.length !== 1 ? 's' : ''} shown`
              }
            </span>
          )}
        </div>
      )}

      {/* ── Chart ── */}
      <TradingChart
        candles={candles}
        markers={displayedMarkers}
        height={440}
        isLoading={loadingChart}
        symbol={isMultiChart ? '' : symbol}
        timeframe={timeframe}
        currentPrice={isMultiChart ? undefined : currentPrice}
        agentColorMap={agentColorMap}
        agentLegend={chartAgentLegend}
        marketType={assetType}
        multiSymbolData={isMultiChart ? displayedMultiSymbolData : undefined}
      />

      {/* ── Agent Portfolios ── */}
      {portfolio.length > 0 && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2">
            <BarChart2 size={15} className="text-blue-400" />
            <span className="text-sm font-semibold text-white">Agent Portfolios</span>
            <span className="text-xs text-slate-500 ml-1">— click the eye icon to filter dashboard by agent</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700/50">
                  <th className="px-3 py-2 w-10" />
                  {['Agent', 'Mode', 'Capital', 'Invested', 'Available', 'Total P&L', 'Win Rate', 'Trades'].map(h => (
                    <th key={h} className="px-4 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {portfolio.map((p, i) => {
                  const color = AGENT_COLORS[i % AGENT_COLORS.length];
                  const active = overlayAgents.includes(p.agent_id);
                  return (
                    <tr
                      key={p.agent_id}
                      className={`border-b border-slate-700/30 transition ${active ? '' : 'hover:bg-slate-700/20'}`}
                      style={active ? { backgroundColor: color + '0d', borderLeft: `3px solid ${color}` } : {}}
                    >
                      {/* Eye toggle */}
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => toggleAgentOverlay(p.agent_id)}
                          title={active ? 'Remove agent filter' : 'Filter dashboard to this agent'}
                          className="p-1 rounded transition hover:bg-slate-600/50"
                        >
                          {active
                            ? <Eye size={14} style={{ color }} />
                            : <EyeOff size={14} className="text-slate-500" />
                          }
                        </button>
                      </td>
                      {/* Agent name with color dot */}
                      <td className="px-4 py-2.5 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                          {p.agent_name}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge label={p.trading_mode} variant={p.trading_mode as any} />
                      </td>
                      <td className="px-4 py-2.5 text-slate-300">${(p.capital ?? 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-slate-300">${(p.invested_capital ?? 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-slate-300">${(p.available_capital ?? 0).toLocaleString()}</td>
                      <td className={`px-4 py-2.5 font-semibold ${pnlColor(p.total_pnl)}`}>{fmt(p.total_pnl)}</td>
                      <td className="px-4 py-2.5 text-slate-300">{(p.win_rate ?? 0).toFixed(1)}%</td>
                      <td className="px-4 py-2.5 text-slate-300">{p.total_trades ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Positions / Trades tabs ── */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden">
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('positions')}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 ${
              activeTab === 'positions' ? 'border-blue-400 text-white' : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            Open Positions
            {displayedPositions.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs">
                {displayedPositions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('trades')}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 ${
              activeTab === 'trades' ? 'border-blue-400 text-white' : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            Trade History
          </button>
          <div className="ml-auto flex items-center pr-4 gap-2">
            {activeTab === 'positions' && (
              <button
                onClick={handleRefreshPrices}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition"
              >
                <RefreshCw size={12} /> Refresh Prices
              </button>
            )}
          </div>
        </div>

        {/* Positions table */}
        {activeTab === 'positions' && (
          <div className="overflow-x-auto">
            {displayedPositions.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Layers size={32} className="mx-auto mb-2 opacity-30" />
                <p>No open positions</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700/50">
                    {['Agent', 'Symbol', 'Qty', 'Avg Entry', 'Current', 'Mkt Value', 'Unr. P&L', 'Mode', 'Opened', 'Action'].map(h => (
                      <th key={h} className="px-4 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedPositions.map(pos => (
                    <tr key={pos.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition">
                      <td className="px-4 py-3 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: agentColorMap[pos.agent_id] ?? '#64748b' }} />
                          {pos.agent_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono font-semibold text-white">{pos.symbol}</td>
                      <td className="px-4 py-3 text-slate-300">{(pos.quantity ?? 0).toFixed(6)}</td>
                      <td className="px-4 py-3 text-slate-300">${(pos.avg_entry_price ?? 0).toFixed(4)}</td>
                      <td className="px-4 py-3 text-slate-300">
                        {pos.current_price ? `$${pos.current_price.toFixed(4)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {pos.market_value ? `$${pos.market_value.toFixed(2)}` : '—'}
                      </td>
                      <td className={`px-4 py-3 font-semibold ${pnlColor(pos.unrealized_pnl)}`}>
                        {fmt(pos.unrealized_pnl)}
                        <span className="text-slate-500 ml-1">({fmtPct(pos.unrealized_pnl_pct)})</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge label={pos.is_paper ? 'Paper' : 'Live'} variant={pos.is_paper ? 'paper' : 'live'} />
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                        {new Date(pos.opened_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleClosePosition(pos.id)}
                          className="px-2.5 py-1 bg-red-500/20 hover:bg-red-500/40 text-red-300 text-xs rounded border border-red-500/30 transition"
                        >
                          Close
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Trade history table */}
        {activeTab === 'trades' && (
          <div className="overflow-x-auto">
            {displayedTrades.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Clock size={32} className="mx-auto mb-2 opacity-30" />
                <p>No trades recorded yet</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700/50">
                    {['Agent', 'Symbol', 'Side', 'Entry Price', 'Exit Price', 'Qty', 'P&L', 'P&L%', 'Mode', 'Time', ''].map(h => (
                      <th key={h} className="px-4 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedTrades.map(t => (
                    <tr key={t.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition">
                      <td className="px-4 py-2.5 font-medium text-white">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: agentColorMap[t.agent_id] ?? '#64748b' }} />
                          {t.agent_name}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 font-mono font-semibold text-white">{t.symbol}</td>
                      <td className="px-4 py-2.5">
                        <span className={`flex items-center gap-1 font-semibold ${
                          t.order_type === 'buy' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {t.order_type === 'buy' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                          {t.order_type?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-300">${t.entry_price?.toFixed(4)}</td>
                      <td className="px-4 py-2.5 text-slate-300">
                        {t.exit_price ? `$${t.exit_price.toFixed(4)}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-slate-300">{t.entry_quantity?.toFixed(6)}</td>
                      <td className={`px-4 py-2.5 font-semibold ${pnlColor(t.pnl)}`}>
                        {t.pnl != null ? fmt(t.pnl) : '—'}
                      </td>
                      <td className={`px-4 py-2.5 ${pnlColor(t.pnl_pct)}`}>
                        {t.pnl_pct != null ? fmtPct(t.pnl_pct) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge label={t.is_paper ? 'Paper' : 'Live'} variant={t.is_paper ? 'paper' : 'live'} />
                      </td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">
                        {new Date(t.entry_timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5">
                        {(t.reasoning || t.notes || t.agent_output) && (
                          <button
                            onClick={() => setReasoningTrade(t)}
                            className="text-slate-400 hover:text-blue-400 transition-colors"
                            title="View reasoning"
                          >
                            <FileText size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── P&L summary row ── */}
      {displayedPnl && displayedPnl.total_trades > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Avg Win',       value: fmt(displayedPnl.avg_win),                                                                   color: 'text-green-400' },
            { label: 'Avg Loss',      value: fmt(-displayedPnl.avg_loss),                                                                  color: 'text-red-400' },
            { label: 'Profit Factor', value: displayedPnl.profit_factor ? displayedPnl.profit_factor.toFixed(2) : '—',                    color: displayedPnl.profit_factor && displayedPnl.profit_factor > 1 ? 'text-green-400' : 'text-red-400' },
            { label: 'Max Drawdown',  value: fmt(displayedPnl.max_drawdown, 2, '-$'),                                                      color: 'text-red-400' },
          ].map(item => (
            <div key={item.label} className="bg-slate-800/40 border border-slate-700 rounded-lg px-4 py-3 text-center">
              <p className="text-xs text-slate-400 mb-1">{item.label}</p>
              <p className={`font-bold text-base ${item.color}`}>{item.value}</p>
            </div>
          ))}
        </div>
      )}
      {/* ── Trade reasoning modal ── */}
      {reasoningTrade && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setReasoningTrade(null)}
        >
          <div
            className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* header */}
            <div className="flex items-start justify-between p-5 border-b border-slate-700">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: agentColorMap[reasoningTrade.agent_id] ?? '#64748b' }} />
                  <span className="text-white font-semibold">{reasoningTrade.agent_name}</span>
                  <span className="text-slate-400 text-sm">·</span>
                  <span className="font-mono text-sm text-slate-300">{reasoningTrade.symbol}</span>
                  <span className={`text-sm font-semibold ${reasoningTrade.order_type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                    {reasoningTrade.order_type?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {new Date(reasoningTrade.entry_timestamp).toLocaleString()} · ${reasoningTrade.entry_price?.toFixed(4)} · {reasoningTrade.entry_quantity?.toFixed(6)} units
                </p>
              </div>
              <button
                onClick={() => setReasoningTrade(null)}
                className="text-slate-400 hover:text-white transition-colors ml-4 flex-shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* body */}
            <div className="overflow-y-auto p-5 space-y-4">
              {/* ── Technicals grid ── */}
              {(() => {
                const ao = reasoningTrade.agent_output;
                const hasTechnicals = ao || reasoningTrade.stop_loss != null || reasoningTrade.take_profit != null;
                if (!hasTechnicals) return null;
                const items: { label: string; value: string; color?: string }[] = [];
                if (ao?.signal) items.push({ label: 'Signal', value: ao.signal.toUpperCase(), color: ao.signal === 'buy' || ao.signal === 'short_buy' ? 'text-green-400' : ao.signal === 'hold' ? 'text-yellow-400' : 'text-red-400' });
                if (ao?.confidence != null) items.push({ label: 'Confidence', value: `${(ao.confidence * 100).toFixed(1)}%`, color: ao.confidence >= 0.7 ? 'text-green-400' : ao.confidence >= 0.4 ? 'text-yellow-400' : 'text-red-400' });
                if (ao?.entry_price != null) items.push({ label: 'Entry Price', value: `$${ao.entry_price.toFixed(4)}` });
                if (reasoningTrade.stop_loss != null || ao?.stop_loss != null) items.push({ label: 'Stop Loss', value: `$${(reasoningTrade.stop_loss ?? ao?.stop_loss)?.toFixed(4)}`, color: 'text-red-400' });
                if (reasoningTrade.take_profit != null || ao?.take_profit != null) items.push({ label: 'Take Profit', value: `$${(reasoningTrade.take_profit ?? ao?.take_profit)?.toFixed(4)}`, color: 'text-green-400' });
                if (ao?.position_size_pct != null) items.push({ label: 'Position Size', value: `${(ao.position_size_pct * 100).toFixed(1)}%` });
                return (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Technicals</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {items.map(item => (
                        <div key={item.label} className="bg-slate-800/60 rounded-lg px-3 py-2">
                          <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                          <p className={`text-sm font-mono font-semibold ${item.color ?? 'text-slate-200'}`}>{item.value}</p>
                        </div>
                      ))}
                    </div>
                    {ao?.alerts && ao.alerts.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {ao.alerts.map((a, i) => (
                          <p key={i} className="text-xs text-yellow-400/80 flex items-center gap-1">
                            <AlertTriangle size={10} className="flex-shrink-0" />{a}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Execution notes ── */}
              {reasoningTrade.notes && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Execution Notes</h3>
                  <p className="text-sm text-slate-400 font-mono">{reasoningTrade.notes}</p>
                </div>
              )}

              {/* ── Full agent reasoning output ── */}
              {(() => {
                const text = reasoningTrade.agent_output?.reasoning || reasoningTrade.reasoning;
                if (!text) return null;
                return (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Agent Reasoning</h3>
                    <div className="bg-slate-950/60 border border-slate-700/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap font-mono">{text}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingDashboard;
